import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import {
  ChessgroundBoardPort,
  boardInputEnabled,
  type BoardIntentHandler,
  type BoardOrientation,
  type BoardProjection,
  type BoardPort,
  type BoardSquare,
} from '@/game/board';
import type { RulesColor, RulesPiece } from '@/game/rules';
import {
  actionArtKey,
  createActionStripDomActor,
  type ActionArtRuntime,
  type ActionStripDomActor,
  type ActionStripDomImageSource,
} from './actionArt';
import type { BattleCue } from './battleCue';
import {
  createCaptureActionPlan,
  type CaptureActionPlan,
} from './captureActionPlan';
import type { PieceTheme } from './pieceTheme/capturedMaterial';

export interface CapyBoardProps {
  readonly projection: BoardProjection;
  readonly onMoveIntent: BoardIntentHandler;
  readonly label: string;
  readonly battleCue?: BattleCue;
  /** Capy is the animated default; standard pieces are deliberately motionless. */
  readonly pieceTheme?: PieceTheme;
  /** Release-owned action art is injected; production defaults to clone rendering. */
  readonly actionArtRuntime?: ActionArtRuntime<ActionStripDomImageSource>;
  /** Optional concise feedback for semantic-grid selection, rejection, and Escape only. */
  readonly onKeyboardFeedback?: (message: string) => void;
}

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;

const THAI_COLOR: Readonly<Record<RulesColor, string>> = {
  w: 'ขาว',
  b: 'ดำ',
};

const THAI_PIECE: Readonly<Record<RulesPiece, string>> = {
  p: 'เบี้ย',
  n: 'ม้า',
  b: 'บิชอป',
  r: 'เรือ',
  q: 'ควีน',
  k: 'คิง',
};

interface BoardPiece {
  readonly color: RulesColor;
  readonly piece: RulesPiece;
}

interface SourceSelection {
  readonly square: BoardSquare;
  readonly revision: number;
}

interface BattlePresentation {
  readonly cue: BattleCue;
  readonly projection: BoardProjection;
}

function capturePresentation(cue: BattleCue, projection: BoardProjection): BattlePresentation {
  const snapshot = Object.freeze({ ...projection.snapshot });
  const immutableProjection = Object.freeze({ ...projection, snapshot });
  return Object.freeze({ cue, projection: immutableProjection });
}

const PIECE_CLASS: Readonly<Record<RulesPiece, string>> = {
  p: 'pawn',
  n: 'knight',
  b: 'bishop',
  r: 'rook',
  q: 'queen',
  k: 'king',
};

type ChessgroundPieceElement = HTMLElement & { readonly cgKey?: string };

function pieceAt(surface: HTMLElement, square: string): ChessgroundPieceElement | undefined {
  return [...surface.querySelectorAll<ChessgroundPieceElement>('piece')]
    .find((piece) => piece.cgKey === square || piece.dataset.key === square);
}

function visualCell(square: string, orientation: BoardOrientation): readonly [number, number] {
  const file = Math.max(0, Math.min(7, (square.charCodeAt(0) || 97) - 97));
  const rank = Math.max(1, Math.min(8, Number(square[1]) || 1));
  return orientation === 'white'
    ? [file, 8 - rank]
    : [7 - file, rank - 1];
}

function positionFxElement(
  element: HTMLElement,
  square: string,
  orientation: BoardOrientation,
): void {
  const [column, row] = visualCell(square, orientation);
  element.style.insetInlineStart = `${column * 12.5}%`;
  element.style.insetBlockStart = `${row * 12.5}%`;
}

function createFxPiece({
  surface,
  square,
  color,
  role,
  orientation,
  battleRole,
  battleArchetype,
}: {
  readonly surface: HTMLElement;
  readonly square: string;
  readonly color: RulesColor;
  readonly role: RulesPiece;
  readonly orientation: BoardOrientation;
  readonly battleRole: 'mover' | 'target' | 'rook' | 'promotion' | 'checked-king';
  readonly battleArchetype?: string;
}): HTMLElement {
  const livePiece = pieceAt(surface, square);
  const piece = (livePiece?.cloneNode(true) ?? document.createElement('piece')) as HTMLElement;
  piece.removeAttribute('style');
  piece.className = [
    color === 'w' ? 'white' : 'black',
    PIECE_CLASS[role],
    'capy-board__fx-piece',
  ].join(' ');
  piece.dataset.battleRole = battleRole;
  piece.dataset.battleSquare = square;
  if (battleRole === 'mover' && battleArchetype !== undefined) {
    piece.dataset.battleArchetype = battleArchetype;
  }
  positionFxElement(piece, square, orientation);
  return piece;
}

interface BattleAttacker {
  readonly element: HTMLElement;
  readonly stripActor?: ActionStripDomActor;
}

function createBattleAttacker({
  surface,
  square,
  color,
  role,
  orientation,
  battleArchetype,
  cueToken,
  runtime,
  signal,
}: {
  readonly surface: HTMLElement;
  readonly square: string;
  readonly color: RulesColor;
  readonly role: RulesPiece;
  readonly orientation: BoardOrientation;
  readonly battleArchetype: string;
  readonly cueToken: string;
  readonly runtime?: ActionArtRuntime<ActionStripDomImageSource>;
  readonly signal: AbortSignal;
}): BattleAttacker {
  const key = actionArtKey(color, role);
  if (runtime !== undefined) {
    const element = createSquareFx('capy-board__fx-attacker is-strip', square, orientation);
    element.dataset.battleRole = 'mover';
    element.dataset.battleArchetype = battleArchetype;
    element.dataset.battleRenderer = 'strip';
    element.dataset.battleAssetKey = key;
    const stripActor = createActionStripDomActor({
      runtime,
      cueToken,
      key,
      host: element,
      signal,
    });
    if (stripActor !== null) {
      stripActor.wrapper.classList.add('capy-board__fx-action-strip');
      stripActor.wrapper.style.width = '100%';
      stripActor.wrapper.style.height = '100%';
      for (const [frameIndex, frame] of stripActor.frames.entries()) {
        frame.style.width = '100%';
        frame.style.height = '100%';
        frame.style.backgroundSize = '100% 600%';
        frame.style.backgroundPosition = `center ${frameIndex * 20}%`;
      }
      return { element, stripActor };
    }
  }

  const element = createFxPiece({
    surface,
    square,
    color,
    role,
    orientation,
    battleRole: 'mover',
    battleArchetype,
  });
  element.dataset.battleRenderer = 'clone';
  element.dataset.battleAssetKey = key;
  return { element };
}

function createSquareFx(
  className: string,
  square: string,
  orientation: BoardOrientation,
): HTMLDivElement {
  const element = document.createElement('div');
  element.className = className;
  element.dataset.battleSquare = square;
  positionFxElement(element, square, orientation);
  return element;
}


function createImpactMark(
  actionPlan: Pick<CaptureActionPlan, 'archetype' | 'impactMark'>,
  square: string,
  orientation: BoardOrientation,
): HTMLDivElement {
  const mark = createSquareFx(
    `capy-board__fx-impact-mark is-${actionPlan.impactMark}`,
    square,
    orientation,
  );
  mark.dataset.battleArchetype = actionPlan.archetype;
  mark.dataset.battleImpactMark = actionPlan.impactMark;
  mark.setAttribute('aria-hidden', 'true');
  mark.setAttribute('inert', '');
  return mark;
}

function createActionAccent(
  actionPlan: Pick<CaptureActionPlan, 'archetype' | 'accentClass'>,
  square: string,
  orientation: BoardOrientation,
): HTMLDivElement {
  const accent = createSquareFx(
    `capy-board__fx-action-accent ${actionPlan.accentClass}`,
    square,
    orientation,
  );
  accent.dataset.battleArchetype = actionPlan.archetype;
  accent.setAttribute('aria-hidden', 'true');
  accent.setAttribute('inert', '');
  return accent;
}

function squareTone(square: string): 'light' | 'dark' {
  const file = Math.max(0, (square.charCodeAt(0) || 97) - 97);
  const rank = Number(square[1]) || 1;
  return (file + rank) % 2 === 0 ? 'light' : 'dark';
}

function travelTransform(
  from: string,
  to: string,
  orientation: BoardOrientation,
  progress = 1,
): string {
  const [fromColumn, fromRow] = visualCell(from, orientation);
  const [toColumn, toRow] = visualCell(to, orientation);
  return `translate(${(toColumn - fromColumn) * 100 * progress}%, ${(toRow - fromRow) * 100 * progress}%)`;
}

function recoilTransform(
  impactSquare: string,
  kingSquare: string,
  orientation: BoardOrientation,
  distance: number,
): string {
  const [impactColumn, impactRow] = visualCell(impactSquare, orientation);
  const [kingColumn, kingRow] = visualCell(kingSquare, orientation);
  const horizontal = Math.sign(kingColumn - impactColumn);
  const vertical = Math.sign(kingRow - impactRow);
  const rotation = horizontal === 0 ? vertical * 2 : horizontal * -2;
  return `translate(${horizontal * distance}%, ${vertical * distance}%) rotate(${rotation}deg)`;
}

function animateFx(
  element: Element,
  keyframes: Keyframe[],
  options: KeyframeAnimationOptions,
  signal: AbortSignal,
  animations: Animation[],
): Promise<unknown> {
  if (typeof element.animate !== 'function') {
    return Promise.resolve();
  }
  const animation = element.animate(keyframes, options);
  animations.push(animation);
  const cancel = () => animation.cancel();
  signal.addEventListener('abort', cancel, { once: true });
  return animation.finished.finally(() => signal.removeEventListener('abort', cancel));
}

function clearBattleFxLayer(fxLayer: HTMLDivElement): void {
  fxLayer.replaceChildren();
  delete fxLayer.dataset.battleCue;
  delete fxLayer.dataset.battleKind;
  delete fxLayer.dataset.battlePhase;
  delete fxLayer.dataset.battleImpactSquare;
  delete fxLayer.dataset.battleAction;
  delete fxLayer.dataset.battleDuration;
  delete fxLayer.dataset.battleDirectionX;
  delete fxLayer.dataset.battleDirectionY;
  delete fxLayer.dataset.battleRenderer;
  delete fxLayer.dataset.battleAssetKey;
  delete fxLayer.dataset.battlePendingCue;
  delete fxLayer.dataset.battleFrame;
  delete fxLayer.dataset.battleHitStopMs;
  delete fxLayer.dataset.battleEffectFamily;
  delete fxLayer.dataset.battleReactionFamily;
}

function isCaptureCue(cue: BattleCue): boolean {
  return cue.capturedPiece !== undefined && cue.captureSquare !== undefined;
}

function checkedKingSquare(projection: BoardProjection): string | undefined {
  const checkedColor = projection.snapshot.checkedColor;
  if (checkedColor === undefined) {
    return undefined;
  }
  for (const [square, piece] of piecesFromFen(projection.snapshot.fen)) {
    if (piece.piece === 'k' && piece.color === checkedColor) {
      return square;
    }
  }
  return undefined;
}

function visualSquares(orientation: BoardOrientation): readonly BoardSquare[] {
  const ranks = orientation === 'white'
    ? [8, 7, 6, 5, 4, 3, 2, 1]
    : [1, 2, 3, 4, 5, 6, 7, 8];
  const files = orientation === 'white' ? FILES : [...FILES].reverse();

  return ranks.flatMap((rank) => files.map((file) => `${file}${rank}`));
}

function piecesFromFen(fen: string): ReadonlyMap<BoardSquare, BoardPiece> {
  const pieces = new Map<BoardSquare, BoardPiece>();
  const rows = fen.split(' ', 1)[0]?.split('/') ?? [];

  for (const [rowIndex, row] of rows.entries()) {
    let fileIndex = 0;
    for (const symbol of row) {
      if (/^[1-8]$/.test(symbol)) {
        fileIndex += Number(symbol);
        continue;
      }

      const file = FILES[fileIndex];
      const piece = symbol.toLowerCase() as RulesPiece;
      if (file !== undefined && THAI_PIECE[piece] !== undefined) {
        pieces.set(`${file}${8 - rowIndex}`, {
          color: symbol === symbol.toUpperCase() ? 'w' : 'b',
          piece,
        });
      }
      fileIndex += 1;
    }
  }

  return pieces;
}

function checkedRulesColor(projection: BoardProjection): RulesColor | undefined {
  if (projection.checkedColor === undefined) {
    return undefined;
  }
  return projection.checkedColor === 'white' ? 'w' : 'b';
}

function accessibleSquareName({
  square,
  piece,
  selected,
  legalTarget,
  checked,
  inputEnabled,
  projection,
}: {
  readonly square: BoardSquare;
  readonly piece?: BoardPiece;
  readonly selected: boolean;
  readonly legalTarget: boolean;
  readonly checked: boolean;
  readonly inputEnabled: boolean;
  readonly projection: BoardProjection;
}): string {
  const state = [
    selected ? 'เลือกต้นทางแล้ว' : undefined,
    legalTarget ? 'ปลายทางที่เดินได้' : undefined,
    projection.lastMove?.from === square ? 'ต้นทางของตาเดินล่าสุด' : undefined,
    projection.lastMove?.to === square ? 'ปลายทางของตาเดินล่าสุด' : undefined,
    checked ? 'คิงกำลังถูกรุก' : undefined,
    !inputEnabled
      ? projection.review
        ? 'กำลังดูย้อนหลัง อ่านอย่างเดียว'
        : projection.promotionPending
          ? 'รอเลือกการเลื่อนขั้น'
          : 'กระดานถูกล็อก'
      : undefined,
  ].filter((value): value is string => value !== undefined);

  return [
    `ช่อง ${square}`,
    piece === undefined ? 'ว่าง' : `${THAI_PIECE[piece.piece]}สี${THAI_COLOR[piece.color]}`,
    ...state,
  ].join(', ');
}

/**
 * Chessground is intentionally kept inside this tiny imperative island. React
 * owns the surrounding semantic controls, while the board receives only a
 * fresh projection from the authoritative session after every move intent.
 */
export function CapyBoard({
  projection,
  onMoveIntent,
  label,
  battleCue,
  pieceTheme = 'capy',
  actionArtRuntime,
  onKeyboardFeedback,
}: CapyBoardProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const fxLayerRef = useRef<HTMLDivElement | null>(null);
  const boardRef = useRef<BoardPort | null>(null);
  const intentRef = useRef<BoardIntentHandler>(onMoveIntent);
  const keyboardFeedbackRef = useRef(onKeyboardFeedback);
  const projectionRef = useRef(projection);
  const pieceThemeRef = useRef(pieceTheme);
  const actionArtRuntimeRef = useRef(actionArtRuntime);
  const orientationCancelledCueIdRef = useRef<string | undefined>(undefined);
  const reviewCancelledCueIdRef = useRef<string | undefined>(undefined);
  const visibilityCancelledCueIdRef = useRef<string | undefined>(undefined);
  const themeCancelledCueIdRef = useRef<string | undefined>(undefined);
  const activePresentationRef = useRef<BattlePresentation | undefined>(undefined);
  const pendingCaptureRef = useRef<BattlePresentation | undefined>(undefined);
  const consumedCueIdRef = useRef<string | undefined>(undefined);
  const squareRefs = useRef(new Map<BoardSquare, HTMLButtonElement>());
  const [focusedSquare, setFocusedSquare] = useState<BoardSquare>(() => visualSquares(projection.orientation)[0]);
  const [selection, setSelection] = useState<SourceSelection>();
  const [presentation, setPresentation] = useState<BattlePresentation>();

  intentRef.current = onMoveIntent;
  keyboardFeedbackRef.current = onKeyboardFeedback;
  projectionRef.current = projection;
  pieceThemeRef.current = pieceTheme;
  actionArtRuntimeRef.current = actionArtRuntime;

  const inputEnabled = boardInputEnabled(projection);
  const presentationCueId = presentation?.cue.id;
  const squares = useMemo(() => visualSquares(projection.orientation), [projection.orientation]);
  const fileLabels = useMemo(
    () => projection.orientation === 'white' ? [...FILES] : [...FILES].reverse(),
    [projection.orientation],
  );
  const rankLabels = useMemo(
    () => projection.orientation === 'white'
      ? ['8', '7', '6', '5', '4', '3', '2', '1']
      : ['1', '2', '3', '4', '5', '6', '7', '8'],
    [projection.orientation],
  );
  const pieces = useMemo(() => piecesFromFen(projection.snapshot.fen), [projection.snapshot.fen]);
  const selectedSquare = selection?.revision === projection.snapshot.revision
    ? selection.square
    : undefined;
  const legalTargets = useMemo(() => new Set(
    selectedSquare === undefined
      ? []
      : projection.snapshot.legalMoves
        .filter((move) => move.from === selectedSquare)
        .map((move) => move.to),
  ), [projection.snapshot.legalMoves, selectedSquare]);
  const presentationProjection = useMemo<BoardProjection>(() => ({
    ...projection,
    ...(selectedSquare === undefined ? {} : { selectedSquare }),
  }), [projection, selectedSquare]);

  const actionArtMotionAllowed = useCallback(() => (
    pieceTheme === 'capy'
    && actionArtRuntime !== undefined
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches !== true
  ), [actionArtRuntime, pieceTheme]);

  const warmActionArtForSquare = useCallback((square: BoardSquare) => {
    if (!inputEnabled || !actionArtMotionAllowed()) return;
    const piece = pieces.get(square);
    if (
      piece === undefined
      || !projection.snapshot.legalMoves.some((move) => move.from === square)
    ) {
      return;
    }
    actionArtRuntime?.warmRoles({ selected: actionArtKey(piece.color, piece.piece) });
  }, [
    actionArtMotionAllowed,
    actionArtRuntime,
    inputEnabled,
    pieces,
    projection.snapshot.legalMoves,
  ]);

  useEffect(() => {
    if (!inputEnabled || !actionArtMotionAllowed()) return;
    const captureRoles = [...new Set(
      projection.snapshot.legalMoves
        .filter((move) => move.isCapture || move.isEnPassant || move.captured !== undefined)
        .map((move) => actionArtKey(move.color, move.piece)),
    )];
    const selected = captureRoles[0];
    const ai = captureRoles[1];
    if (selected === undefined && ai === undefined) return;
    actionArtRuntime?.warmRoles({
      ...(selected === undefined ? {} : { selected }),
      ...(ai === undefined ? {} : { ai }),
    });
  }, [
    actionArtMotionAllowed,
    actionArtRuntime,
    inputEnabled,
    projection.snapshot.legalMoves,
    projection.snapshot.revision,
  ]);

  useEffect(() => {
    if (!inputEnabled) {
      setSelection(undefined);
      return;
    }
    setSelection((current) => current?.revision === projection.snapshot.revision ? current : undefined);
  }, [inputEnabled, projection.snapshot.revision]);

  useEffect(() => {
    if (battleCue === undefined) {
      activePresentationRef.current = undefined;
      pendingCaptureRef.current = undefined;
      delete fxLayerRef.current?.dataset.battlePendingCue;
      setPresentation(undefined);
      return;
    }
    if (consumedCueIdRef.current === battleCue.id) return;
    consumedCueIdRef.current = battleCue.id;

    if (
      pieceTheme !== 'capy'
      || projection.review
      || document.visibilityState === 'hidden'
      || battleCue.revision !== projection.snapshot.revision
    ) {
      pendingCaptureRef.current = undefined;
      return;
    }

    const incoming = capturePresentation(battleCue, projection);
    const active = activePresentationRef.current;
    if (active !== undefined) {
      const sameEpoch = active.cue.gameId === battleCue.gameId && active.cue.gameEpoch === battleCue.gameEpoch;
      if (!sameEpoch) {
        pendingCaptureRef.current = undefined;
        delete fxLayerRef.current?.dataset.battlePendingCue;
        activePresentationRef.current = incoming;
        setPresentation(incoming);
        return;
      }
      if (isCaptureCue(battleCue)) {
        pendingCaptureRef.current = incoming;
        if (fxLayerRef.current !== null) {
          fxLayerRef.current.dataset.battlePendingCue = battleCue.id;
        }
      } else if (!isCaptureCue(active.cue)) {
        activePresentationRef.current = incoming;
        setPresentation(incoming);
      }
      return;
    }

    activePresentationRef.current = incoming;
    setPresentation(incoming);
  }, [battleCue, pieceTheme, projection.review, projection.snapshot.revision]);

  useEffect(() => {
    const element = mountRef.current;
    if (element === null) {
      return undefined;
    }

    const board = new ChessgroundBoardPort({
      element,
      initialProjection: presentationProjection,
      onMoveIntent: (intent) => intentRef.current(intent),
    });
    boardRef.current = board;

    return () => {
      board.destroy();
      if (boardRef.current === board) {
        boardRef.current = null;
      }
    };
    // Creation is intentionally once per mounted board. New props use render().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fxLayer = fxLayerRef.current;
    const surface = mountRef.current;
    if (boardRef.current === null) {
      return undefined;
    }

    const cue = presentation?.cue;
    const cueProjection = presentation?.projection ?? projectionRef.current;
    if (pieceTheme !== 'capy') {
      if (cue !== undefined) themeCancelledCueIdRef.current = cue.id;
      activePresentationRef.current = undefined;
      pendingCaptureRef.current = undefined;
      if (fxLayer !== null) clearBattleFxLayer(fxLayer);
      return undefined;
    }
    if (projection.review) {
      if (cue !== undefined) reviewCancelledCueIdRef.current = cue.id;
      activePresentationRef.current = undefined;
      pendingCaptureRef.current = undefined;
      if (fxLayer !== null) clearBattleFxLayer(fxLayer);
      return undefined;
    }
    if (
      cue === undefined
      || cue.revision !== cueProjection.snapshot.revision
      || fxLayer === null
      || surface === null
    ) {
      if (cue !== undefined && activePresentationRef.current?.cue.id === cue.id) {
        activePresentationRef.current = undefined;
      }
      pendingCaptureRef.current = undefined;
      if (fxLayer !== null) clearBattleFxLayer(fxLayer);
      return undefined;
    }
    if (reviewCancelledCueIdRef.current === cue.id) {
      clearBattleFxLayer(fxLayer);
      return undefined;
    }
    reviewCancelledCueIdRef.current = undefined;
    if (orientationCancelledCueIdRef.current === cue.id) {
      clearBattleFxLayer(fxLayer);
      return undefined;
    }
    orientationCancelledCueIdRef.current = undefined;
    if (visibilityCancelledCueIdRef.current === cue.id) {
      clearBattleFxLayer(fxLayer);
      return undefined;
    }
    visibilityCancelledCueIdRef.current = undefined;
    if (themeCancelledCueIdRef.current === cue.id) {
      clearBattleFxLayer(fxLayer);
      return undefined;
    }
    themeCancelledCueIdRef.current = undefined;
    if (document.visibilityState === 'hidden') {
      visibilityCancelledCueIdRef.current = cue.id;
      clearBattleFxLayer(fxLayer);
      return undefined;
    }

    const orientation = cueProjection.orientation;
    const cueActionArtRuntime = actionArtRuntimeRef.current;
    const controller = new AbortController();
    const animations: Animation[] = [];
    const reducedMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const reducedMotion = reducedMotionQuery?.matches ?? false;
    const capture = isCaptureCue(cue);
    const confirmationSquare = cue.captureSquare ?? cue.to;
    const actionPlan = capture
      ? createCaptureActionPlan(cue.moverPiece, {
        source: visualCell(cue.from, orientation),
        target: visualCell(cue.captureSquare ?? cue.to, orientation),
        destination: visualCell(cue.to, orientation),
      })
      : undefined;
    const attacker = createBattleAttacker({
      surface,
      square: cue.from,
      color: cue.moverColor,
      role: cue.moverPiece,
      orientation,
      battleArchetype: actionPlan?.archetype ?? `${PIECE_CLASS[cue.moverPiece]}-move`,
      cueToken: cue.id,
      runtime: capture && !reducedMotion ? cueActionArtRuntime : undefined,
      signal: controller.signal,
    });
    const mover = attacker.element;
    if (cue.promotion !== undefined) mover.classList.add('is-promotion');
    const target = cue.capturedPiece === undefined || cue.captureSquare === undefined
      ? undefined
      : createFxPiece({
        surface,
        square: cue.captureSquare,
        color: cue.moverColor === 'w' ? 'b' : 'w',
        role: cue.capturedPiece,
        orientation,
        battleRole: 'target',
      });
    const shadow = !capture
      ? undefined
      : createSquareFx('capy-board__fx-shadow', cue.from, orientation);
    if (shadow !== undefined) {
      shadow.dataset.battleRole = 'shadow';
      shadow.dataset.battlePlane = 'ground';
      shadow.setAttribute('aria-hidden', 'true');
      shadow.setAttribute('inert', '');
      shadow.inert = true;
    }
    const rook = cue.castle === undefined
      ? undefined
      : createFxPiece({
        surface,
        square: cue.castle.rookFrom,
        color: cue.moverColor,
        role: 'r',
        orientation,
        battleRole: 'rook',
      });
    const destinationCover = createSquareFx(
      `capy-board__fx-cover is-${squareTone(cue.to)}`,
      cue.to,
      orientation,
    );
    const rookDestinationCover = cue.castle === undefined
      ? undefined
      : createSquareFx(
        `capy-board__fx-cover is-${squareTone(cue.castle.rookTo)}`,
        cue.castle.rookTo,
        orientation,
      );

    fxLayer.replaceChildren(...[
      destinationCover,
      rookDestinationCover,
      shadow,
      target,
      mover,
      rook,
    ].filter((element): element is HTMLElement => element !== undefined));
    fxLayer.dataset.battleCue = cue.id;
    fxLayer.dataset.battleKind = cue.kind;
    if (capture) fxLayer.dataset.battleImpactSquare = cue.captureSquare;
    fxLayer.dataset.battleRenderer = mover.dataset.battleRenderer ?? 'clone';
    fxLayer.dataset.battleAssetKey = mover.dataset.battleAssetKey ?? actionArtKey(cue.moverColor, cue.moverPiece);

    let complete = false;
    let switchedToReducedMotion = false;
    let replacementConfirmationController: AbortController | undefined;
    const finish = (cancel: boolean) => {
      if (complete) return;
      complete = true;
      document.removeEventListener('visibilitychange', cancelForVisibility);
      reducedMotionQuery?.removeEventListener('change', cancelForMotionPreference);
      if (cancel) {
        if (activePresentationRef.current?.cue.id === cue.id) {
          pendingCaptureRef.current = undefined;
          activePresentationRef.current = undefined;
        }
        controller.abort();
        replacementConfirmationController?.abort();
        if (attacker.stripActor === undefined) cueActionArtRuntime?.abortPending();
      } else {
        attacker.stripActor?.destroy();
      }
      clearBattleFxLayer(fxLayer);
      if (!cancel && activePresentationRef.current?.cue.id === cue.id) {
        const next = pendingCaptureRef.current;
        pendingCaptureRef.current = undefined;
        activePresentationRef.current = next;
        setPresentation(next);
      }
    };
    const cancelForVisibility = () => {
      if (document.visibilityState === 'hidden') {
        visibilityCancelledCueIdRef.current = cue.id;
        pendingCaptureRef.current = undefined;
        finish(true);
      }
    };
    const cancelForMotionPreference = () => {
      if (reducedMotion) {
        finish(true);
        return;
      }
      if (complete || switchedToReducedMotion || reducedMotionQuery?.matches !== true) {
        return;
      }

      switchedToReducedMotion = true;
      controller.abort();
      if (attacker.stripActor === undefined) cueActionArtRuntime?.abortPending();
      clearBattleFxLayer(fxLayer);
      fxLayer.dataset.battleCue = cue.id;
      fxLayer.dataset.battleKind = cue.kind;
      if (capture) fxLayer.dataset.battleImpactSquare = cue.captureSquare;
      fxLayer.dataset.battlePhase = 'confirmation';
      const confirmation = createSquareFx('capy-board__fx-confirmation', confirmationSquare, orientation);
      confirmation.setAttribute('aria-hidden', 'true');
      confirmation.setAttribute('inert', '');
      fxLayer.append(confirmation);
      replacementConfirmationController = new AbortController();
      const replacementController = replacementConfirmationController;
      void Promise.allSettled([animateFx(confirmation, [
        { opacity: 0.82 },
        { opacity: 0.82 },
      ], { duration: 180, easing: 'linear', fill: 'forwards' }, replacementController.signal, animations)])
        .finally(() => {
          if (!replacementController.signal.aborted) finish(false);
        });
    };
    document.addEventListener('visibilitychange', cancelForVisibility);
    reducedMotionQuery?.addEventListener('change', cancelForMotionPreference);

    void (async () => {
      try {
        if (reducedMotion) {
          fxLayer.dataset.battlePhase = 'confirmation';
          fxLayer.replaceChildren();
          const confirmation = createSquareFx('capy-board__fx-confirmation', confirmationSquare, orientation);
          confirmation.setAttribute('aria-hidden', 'true');
          confirmation.setAttribute('inert', '');
          fxLayer.append(confirmation);
          await Promise.allSettled([
            animateFx(confirmation, [
              { opacity: 0.82 },
              { opacity: 0.82 },
            ], { duration: 180, easing: 'linear', fill: 'forwards' }, controller.signal, animations),
          ]);
          return;
        }

        if (capture && actionPlan !== undefined) {
          fxLayer.dataset.battleAction = actionPlan.archetype;
          fxLayer.dataset.battleDuration = String(actionPlan.totalDuration);
          fxLayer.dataset.battleDirectionX = String(actionPlan.direction.deltaX);
          fxLayer.dataset.battleDirectionY = String(actionPlan.direction.deltaY);
          fxLayer.dataset.battleEffectFamily = actionPlan.effectFamily;
          fxLayer.dataset.battleReactionFamily = actionPlan.reactionFamily;

          let promoted: HTMLElement | undefined;
          for (const phase of actionPlan.phases) {
            fxLayer.dataset.battlePhase = phase.name;
            fxLayer.dataset.battleFrame = `F0${phase.frame + 1}`;
            attacker.stripActor?.showFrame(phase.frame, phase.frameBlendMs);
            if (phase.hitStopMs !== undefined) {
              fxLayer.dataset.battleHitStopMs = String(phase.hitStopMs);
            } else {
              delete fxLayer.dataset.battleHitStopMs;
            }
            const phaseAnimations: Promise<unknown>[] = [];
            const transientContactFx: HTMLElement[] = [];
            const moverFrames = cue.promotion !== undefined && phase.name !== 'anticipation' && phase.name !== 'travel'
              ? phase.moverKeyframes.map((frame, index, frames) => ({
                ...frame,
                opacity: phase.name === 'contact'
                  ? 1 - (index / Math.max(1, frames.length - 1))
                  : 0,
              }))
              : phase.moverKeyframes;
            phaseAnimations.push(animateFx(
              mover,
              moverFrames,
              {
                duration: phase.duration,
                easing: phase.name === 'travel'
                  ? 'cubic-bezier(0.77, 0, 0.175, 1)'
                  : 'cubic-bezier(0.23, 1, 0.32, 1)',
                fill: 'forwards',
              },
              controller.signal,
              animations,
            ));
            if (shadow !== undefined) {
              phaseAnimations.push(animateFx(
                shadow,
                phase.shadowKeyframes,
                { duration: phase.duration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' },
                controller.signal,
                animations,
              ));
            }
            if (target !== undefined) {
              phaseAnimations.push(animateFx(
                target,
                actionPlan.targetKeyframes[phase.name],
                { duration: phase.duration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' },
                controller.signal,
                animations,
              ));
            }
            if (rook !== undefined && cue.castle !== undefined) {
              const rookTravel = travelTransform(cue.castle.rookFrom, cue.castle.rookTo, orientation);
              const atOrigin = 'translate(0, 0) scale(1)';
              const crouched = 'translate(-3%, 0) scale(1.03, 0.97)';
              const atDestination = `${rookTravel} scale(1)`;
              const rookFrames: Keyframe[] = phase.name === 'anticipation'
                ? [{ opacity: 1, transform: atOrigin }, { opacity: 1, transform: crouched }]
                : phase.name === 'travel'
                  ? [{ opacity: 1, transform: crouched }, { opacity: 1, transform: atDestination }]
                  : phase.name === 'contact'
                    ? [
                      { opacity: 1, transform: atDestination },
                      { opacity: 1, transform: `${rookTravel} translateY(3%) scale(0.98, 1.04)`, offset: 0.42 },
                      { opacity: 1, transform: atDestination },
                    ]
                    : [{ opacity: 1, transform: atDestination }, { opacity: 1, transform: atDestination }];
              phaseAnimations.push(animateFx(
                rook,
                rookFrames,
                { duration: phase.duration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' },
                controller.signal,
                animations,
              ));
            }

            if (phase.name === 'contact') {
              const impactSquare = cue.captureSquare ?? cue.to;
              const impactMark = createImpactMark(actionPlan, impactSquare, orientation);
              const accent = createActionAccent(
                actionPlan,
                impactSquare,
                orientation,
              );
              const roleEffects = actionPlan.effectLayers.map((layer) => {
                const { plane, className } = layer;
                const effect = createSquareFx(
                  `capy-board__fx-role-effect ${className}`,
                  impactSquare,
                  orientation,
                );
                effect.dataset.battleRoleEffect = className;
                effect.dataset.battleEffectFamily = actionPlan.effectFamily;
                effect.dataset.battlePlane = plane;
                effect.dataset.battleEffectDelay = String(layer.delayMs);
                effect.dataset.battleEffectDuration = String(layer.durationMs);
                effect.setAttribute('aria-hidden', 'true');
                effect.setAttribute('inert', '');
                effect.inert = true;
                return { effect, layer };
              });
              fxLayer.append(...roleEffects.map(({ effect }) => effect), impactMark, accent);
              transientContactFx.push(...roleEffects.map(({ effect }) => effect), impactMark, accent);
              phaseAnimations.push(
                animateFx(impactMark, actionPlan.impactKeyframes, { duration: phase.duration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations),
                animateFx(accent, actionPlan.accentKeyframes, { duration: phase.duration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations),
                ...roleEffects.map(({ effect, layer }) => animateFx(effect, [...layer.keyframes], {
                  duration: layer.durationMs,
                  delay: layer.delayMs,
                  easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
                  fill: 'forwards',
                }, controller.signal, animations)),
              );

              if (cue.promotion !== undefined) {
                promoted = createFxPiece({
                  surface,
                  square: cue.to,
                  color: cue.moverColor,
                  role: cue.promotion,
                  orientation,
                  battleRole: 'promotion',
                });
                promoted.classList.add('is-promotion');
                promoted.style.opacity = '0';
                fxLayer.append(promoted);
                phaseAnimations.push(animateFx(promoted, [
                  { opacity: 0, transform: 'translateY(8%) scale(0.78)' },
                  { opacity: 1, transform: 'translateY(-3%) scale(1.1)', offset: 0.58 },
                  { opacity: 1, transform: 'translateY(0) scale(1)' },
                ], { duration: phase.duration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations));
              }

              if (cue.terminal) {
                const terminalHalo = createSquareFx('capy-board__fx-terminal-halo', impactSquare, orientation);
                fxLayer.append(terminalHalo);
                transientContactFx.push(terminalHalo);
                phaseAnimations.push(animateFx(terminalHalo, [
                  { opacity: 0, transform: 'scale(0.76)' },
                  { opacity: 0.76, transform: 'scale(1.1)', offset: 0.42 },
                  { opacity: 0, transform: 'scale(1.28)' },
                ], { duration: phase.duration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations));
              }

              const kingSquare = cue.check ? checkedKingSquare(cueProjection) : undefined;
              const checkedKingColor = cueProjection.snapshot.checkedColor;
              if (kingSquare !== undefined && checkedKingColor !== undefined) {
                const checkedKing = createFxPiece({
                  surface,
                  square: kingSquare,
                  color: checkedKingColor,
                  role: 'k',
                  orientation,
                  battleRole: 'checked-king',
                });
                const pulse = createSquareFx('capy-board__fx-check', kingSquare, orientation);
                const recoil = recoilTransform(impactSquare, kingSquare, orientation, 8);
                const settle = recoilTransform(impactSquare, kingSquare, orientation, -2);
                fxLayer.append(checkedKing, pulse);
                transientContactFx.push(checkedKing, pulse);
                phaseAnimations.push(
                  animateFx(checkedKing, [
                    { opacity: 1, transform: 'translate(0, 0) scale(1)' },
                    { opacity: 0.92, transform: `${recoil} scale(0.96)`, offset: 0.42 },
                    { opacity: 1, transform: `${settle} scale(1)` },
                  ], { duration: phase.duration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations),
                  animateFx(pulse, [
                    { opacity: 0, transform: 'scale(0.9)' },
                    { opacity: 0.9, transform: 'scale(1.08)', offset: 0.38 },
                    { opacity: 0, transform: 'scale(1.18)' },
                  ], { duration: phase.duration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations),
                );
              }
            } else if (promoted !== undefined) {
              const promotedOpacity = phase.name === 'settle' ? 0 : 1;
              phaseAnimations.push(animateFx(promoted, [
                { opacity: 1, transform: 'translateY(-3%) scale(1.04)' },
                { opacity: promotedOpacity, transform: `translateY(0) scale(${phase.name === 'hold' ? 1.02 : 1})` },
              ], { duration: phase.duration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations));
            }

            await Promise.allSettled(phaseAnimations);
            if (controller.signal.aborted) return;
            transientContactFx.forEach((element) => element.remove());
          }
          return;
        }

        const simpleCapture = target !== undefined;
        const promotion = cue.promotion !== undefined;
        const anticipationDuration = simpleCapture ? 45 : promotion ? 35 : 25;
        const travelDuration = simpleCapture ? 120 : promotion ? 140 : cue.castle === undefined ? 100 : 145;
        const impactDuration = simpleCapture ? 210 : promotion ? 140 : 55;
        const followThroughDuration = simpleCapture ? 40 : promotion ? 50 : 30;
        const travel = travelTransform(cue.from, cue.to, orientation);
        const travelMid = travelTransform(cue.from, cue.to, orientation, 0.72);
        const anticipationBase = cue.moverPiece === 'n'
          ? 'translate(0, 8%) rotate(5deg) scale(0.92)'
          : cue.moverPiece === 'r'
            ? 'translate(-3%, 1%) scale(0.94, 0.9)'
            : cue.moverPiece === 'b'
              ? 'translate(-4%, 5%) rotate(-4deg) scale(0.92)'
              : cue.moverPiece === 'q'
                ? 'translate(-5%, 3%) rotate(3deg) scale(0.93)'
                : cue.moverPiece === 'k'
                  ? 'translate(-3%, 3%) rotate(-3deg) scale(0.93)'
                  : 'translate(-6%, 1%) scale(0.92)';
        const anticipation = simpleCapture
          ? `${anticipationBase} scale(0.9)`
          : anticipationBase;
        const moverMidBaseTransform = cue.moverPiece === 'n'
          ? `${travelMid} translateY(-20%) rotate(-7deg) scale(1.16)`
          : cue.moverPiece === 'r'
            ? `${travelMid} scale(1.16, 1.01)`
            : cue.moverPiece === 'b'
              ? `${travelMid} rotate(6deg) scale(1.15)`
              : cue.moverPiece === 'q'
                ? `${travelMid} rotate(-5deg) scale(1.16)`
                : cue.moverPiece === 'k'
                  ? `${travelMid} rotate(5deg) scale(1.13)`
                  : `${travelMid} scale(1.14, 0.98)`;
        const moverMidTransform = simpleCapture
          ? `${moverMidBaseTransform} translateY(-10%) scale(1.1)`
          : moverMidBaseTransform;

        fxLayer.dataset.battlePhase = 'anticipation';
        const anticipationAnimations = [
          animateFx(mover, [
            { opacity: 1, transform: 'translate(0, 0) scale(1)' },
            { opacity: 1, transform: anticipation },
          ], { duration: anticipationDuration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations),
        ];
        if (rook !== undefined) {
          anticipationAnimations.push(animateFx(rook, [
            { opacity: 1, transform: 'translate(0, 0) scale(1)' },
            { opacity: 1, transform: 'translate(-3%, 0) scale(1.03, 0.97)' },
          ], { duration: anticipationDuration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations));
        }
        await Promise.allSettled(anticipationAnimations);
        if (controller.signal.aborted) return;

        fxLayer.dataset.battlePhase = 'travel';
        const travelAnimations = [
          animateFx(mover, [
            { opacity: 1, transform: anticipation },
            { opacity: 1, transform: moverMidTransform, offset: 0.72 },
            { opacity: 1, transform: `${travel} scale(${simpleCapture ? 1.18 : 1.11})` },
          ], { duration: travelDuration, easing: 'cubic-bezier(0.77, 0, 0.175, 1)', fill: 'forwards' }, controller.signal, animations),
        ];
        if (rook !== undefined && cue.castle !== undefined) {
          travelAnimations.push(animateFx(rook, [
            { opacity: 1, transform: 'translate(-3%, 0) scale(1.03, 0.97)' },
            { opacity: 1, transform: `${travelTransform(cue.castle.rookFrom, cue.castle.rookTo, orientation)} scale(1.04)` },
          ], { duration: travelDuration, easing: 'cubic-bezier(0.77, 0, 0.175, 1)', fill: 'forwards' }, controller.signal, animations));
        }
        await Promise.allSettled(travelAnimations);
        if (controller.signal.aborted) return;

        fxLayer.dataset.battlePhase = 'impact';
        const promoted = cue.promotion === undefined
          ? undefined
          : createFxPiece({
            surface,
            square: cue.to,
            color: cue.moverColor,
            role: cue.promotion,
            orientation,
            battleRole: 'promotion',
          });
        if (promoted !== undefined) {
          promoted.classList.add('is-promotion');
          promoted.style.opacity = '0';
          fxLayer.append(promoted);
        }
        const moverImpact: Keyframe[] = promotion
          ? simpleCapture
            ? [
              { opacity: 1, transform: `${travel} scale(1.18)` },
              { opacity: 0.78, transform: `${travel} translateY(-8%) scale(1.28)`, offset: 0.18 },
              { opacity: 0.44, transform: `${travel} translateY(3%) scale(0.97, 1.1)`, offset: 0.48 },
              { opacity: 0, transform: `${travel} scale(0.96)` },
            ]
            : [
              { opacity: 1, transform: `${travel} scale(1.11)` },
              { opacity: 0.62, transform: `${travel} scale(1.02)`, offset: 0.42 },
              { opacity: 0, transform: `${travel} scale(0.96)` },
            ]
          : simpleCapture
            ? [
              { opacity: 1, transform: `${travel} scale(1.18)` },
              { opacity: 1, transform: `${travel} translateY(-8%) scale(1.28)`, offset: 0.18 },
              { opacity: 1, transform: `${travel} translateY(3%) scale(0.97, 1.1)`, offset: 0.48 },
              { opacity: 1, transform: `${travel} scale(1.12)`, offset: 0.76 },
              { opacity: 1, transform: `${travel} scale(1.07)` },
            ]
            : cue.castle === undefined
            ? [
              { opacity: 1, transform: `${travel} scale(1.11)` },
              { opacity: 1, transform: `${travel} scale(0.99)`, offset: 0.42 },
              { opacity: 1, transform: `${travel} scale(1.07)` },
            ]
            : [
              { opacity: 1, transform: `${travel} scale(1.11)` },
              { opacity: 1, transform: `${travel} translateY(3%) scale(0.98, 1.04)`, offset: 0.42 },
              { opacity: 1, transform: `${travel} scale(1.03)` },
            ];
        const impactAnimations: Promise<unknown>[] = [
          animateFx(mover, moverImpact, { duration: impactDuration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations),
        ];
        if (promoted !== undefined) {
          impactAnimations.push(animateFx(promoted, [
            { opacity: 0, transform: 'scale(0.88)' },
            { opacity: 1, transform: 'scale(1.07)', offset: 0.42 },
            { opacity: 1, transform: 'scale(1)' },
          ], { duration: impactDuration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations));
        }
        if (cue.castle === undefined) {
          const dust = createSquareFx('capy-board__fx-dust', cue.to, orientation);
          fxLayer.append(dust);
          impactAnimations.push(animateFx(dust, [
            { opacity: 0, transform: 'translateY(8%) scale(0.96)' },
            { opacity: 0.72, transform: 'translateY(0) scale(1.04)' },
            { opacity: 0, transform: 'translateY(-10%) scale(1)' },
          ], { duration: impactDuration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations));
        }

        if (rook !== undefined && cue.castle !== undefined) {
          const rookTravel = travelTransform(cue.castle.rookFrom, cue.castle.rookTo, orientation);
          impactAnimations.push(animateFx(rook, [
            { opacity: 1, transform: `${rookTravel} scale(1.04)` },
            { opacity: 1, transform: `${rookTravel} translateY(3%) scale(0.98, 1.04)`, offset: 0.42 },
            { opacity: 1, transform: `${rookTravel} scale(1.03)` },
          ], { duration: impactDuration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations));
        }

        if (cue.terminal) {
          const terminalHalo = createSquareFx('capy-board__fx-terminal-halo', confirmationSquare, orientation);
          fxLayer.append(terminalHalo);
          impactAnimations.push(animateFx(terminalHalo, [
            { opacity: 0, transform: 'scale(0.76)' },
            { opacity: 0.72, transform: 'scale(1.08)', offset: 0.4 },
            { opacity: 0, transform: 'scale(1.22)' },
          ], { duration: impactDuration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations));
        }

        const kingSquare = cue.check ? checkedKingSquare(cueProjection) : undefined;
        const checkedKingColor = cueProjection.snapshot.checkedColor;
        if (kingSquare !== undefined && checkedKingColor !== undefined) {
          const checkedKing = createFxPiece({
            surface,
            square: kingSquare,
            color: checkedKingColor,
            role: 'k',
            orientation,
            battleRole: 'checked-king',
          });
          const pulse = createSquareFx('capy-board__fx-check', kingSquare, orientation);
          const recoil = recoilTransform(cue.to, kingSquare, orientation, 7);
          const settle = recoilTransform(cue.to, kingSquare, orientation, -2);
          fxLayer.append(checkedKing, pulse);
          impactAnimations.push(
            animateFx(checkedKing, [
              { opacity: 1, transform: 'translate(0, 0) scale(1)' },
              { opacity: 0.94, transform: `${recoil} scale(0.97)`, offset: 0.38 },
              { opacity: 1, transform: `${settle} scale(1)` },
            ], { duration: impactDuration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations),
            animateFx(pulse, [
              { opacity: 0, transform: 'translateX(0) scale(0.96)' },
              { opacity: 0.9, transform: 'translateX(-4%) scale(1.03)' },
              { opacity: 0, transform: 'translateX(4%) scale(1)' },
            ], { duration: impactDuration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations),
          );
        }
        await Promise.allSettled(impactAnimations);
        if (controller.signal.aborted) return;

        fxLayer.dataset.battlePhase = 'follow-through';
        const landingPiece = promoted ?? mover;
        const landingFollowThrough: Keyframe[] = promotion
          ? [
            { opacity: 1, transform: 'scale(1.07)' },
            { opacity: 1, transform: 'scale(1)' },
          ]
          : [
            { opacity: 1, transform: `${travel} scale(1.07)` },
            { opacity: 1, transform: `${travel} scale(1)` },
          ];
        const followThroughAnimations = [
          animateFx(landingPiece, landingFollowThrough, { duration: followThroughDuration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations),
        ];
        if (rook !== undefined && cue.castle !== undefined) {
          const rookTravel = travelTransform(cue.castle.rookFrom, cue.castle.rookTo, orientation);
          followThroughAnimations.push(animateFx(rook, [
            { opacity: 1, transform: `${rookTravel} scale(1.04)` },
            { opacity: 1, transform: `${rookTravel} scale(1)` },
          ], { duration: followThroughDuration, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }, controller.signal, animations));
        }
        await Promise.allSettled(followThroughAnimations);
      } finally {
        if (!switchedToReducedMotion) finish(false);
      }
    })();

    return () => {
      if (projectionRef.current.orientation !== orientation) {
        orientationCancelledCueIdRef.current = cue.id;
      }
      if (pieceThemeRef.current !== 'capy') {
        themeCancelledCueIdRef.current = cue.id;
      }
      document.removeEventListener('visibilitychange', cancelForVisibility);
      finish(true);
    };
  }, [pieceTheme, presentation, presentationCueId, projection.orientation, projection.review]);

  useEffect(() => {
    boardRef.current?.render(presentationProjection);
  }, [presentationProjection]);

  const clearSelection = useCallback(() => {
    setSelection(undefined);
  }, []);

  const handleBoardPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    clearSelection();
    const target = event.target;
    const piece = target instanceof Element
      ? target.closest<ChessgroundPieceElement>('piece')
      : null;
    const square = piece?.cgKey ?? piece?.dataset.key;
    if (square !== undefined) warmActionArtForSquare(square);
  }, [clearSelection, warmActionArtForSquare]);

  const reportKeyboardFeedback = useCallback((message: string) => {
    keyboardFeedbackRef.current?.(message);
  }, []);

  const moveFocus = useCallback((square: BoardSquare, rowDelta: number, columnDelta: number) => {
    const currentIndex = squares.indexOf(square);
    if (currentIndex < 0) {
      return;
    }

    const row = Math.floor(currentIndex / 8);
    const column = currentIndex % 8;
    const nextRow = row + rowDelta;
    const nextColumn = column + columnDelta;
    if (nextRow < 0 || nextRow > 7 || nextColumn < 0 || nextColumn > 7) {
      return;
    }

    const nextIndex = (nextRow * 8) + nextColumn;
    const nextSquare = squares[nextIndex];
    if (nextSquare === undefined) {
      return;
    }
    setFocusedSquare(nextSquare);
    squareRefs.current.get(nextSquare)?.focus();
  }, [squares]);

  const activateSquare = useCallback((square: BoardSquare) => {
    if (!inputEnabled) {
      reportKeyboardFeedback('กระดานถูกล็อก');
      return;
    }

    if (selectedSquare === undefined) {
      if (projection.snapshot.legalMoves.some((move) => move.from === square)) {
        warmActionArtForSquare(square);
        setSelection({ square, revision: projection.snapshot.revision });
        reportKeyboardFeedback(`เลือกต้นทาง ${square} แล้ว`);
      } else {
        reportKeyboardFeedback(`ช่อง ${square} ไม่มีตัวหมากที่เดินได้`);
      }
      return;
    }

    if (!legalTargets.has(square)) {
      reportKeyboardFeedback(`จาก ${selectedSquare} ไป ${square} เดินไม่ได้`);
      return;
    }

    const nextProjection = intentRef.current({
      expectedRevision: projection.snapshot.revision,
      from: selectedSquare,
      to: square,
    });
    setSelection(undefined);
    boardRef.current?.render(nextProjection);
    if (
      nextProjection.snapshot.revision === projection.snapshot.revision
      && !nextProjection.promotionPending
    ) {
      reportKeyboardFeedback(`ตาเดิน ${selectedSquare} ไป ${square} ถูกปฏิเสธ`);
    }
  }, [
    inputEnabled,
    legalTargets,
    projection.snapshot.legalMoves,
    projection.snapshot.revision,
    reportKeyboardFeedback,
    selectedSquare,
    warmActionArtForSquare,
  ]);

  const onSquareKeyDown = useCallback((event: KeyboardEvent<HTMLButtonElement>, square: BoardSquare) => {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        moveFocus(square, 0, -1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        moveFocus(square, 0, 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveFocus(square, -1, 0);
        break;
      case 'ArrowDown':
        event.preventDefault();
        moveFocus(square, 1, 0);
        break;
      case 'Escape':
        event.preventDefault();
        clearSelection();
        reportKeyboardFeedback(
          selectedSquare === undefined
            ? 'ยังไม่ได้เลือกต้นทาง'
            : `ยกเลิกการเลือก ${selectedSquare} แล้ว`,
        );
        break;
      default:
        break;
    }
  }, [clearSelection, moveFocus, reportKeyboardFeedback, selectedSquare]);

  return (
    <section className="capy-board__accessible" aria-label={label}>
      <div className={`capy-board is-${pieceTheme}`} data-piece-theme={pieceTheme} onPointerDownCapture={handleBoardPointerDown}>
        <div className="capy-board__surface" ref={mountRef} />
        <div
          className="capy-board__fx-layer"
          ref={fxLayerRef}
          aria-hidden="true"
          inert
          style={{ pointerEvents: 'none' }}
        />
        {projection.coordinates && (
          <div className="capy-board__coordinate-grid" aria-hidden="true" data-coordinate-layer="owned">
            {fileLabels.map((file, index) => (
              <span
                className="capy-board__coordinate-badge is-file"
                data-board-coordinate={`file-${file}`}
                data-coordinate-axis="file"
                data-coordinate-tone="fixed"
                key={`file-${file}`}
                style={{ gridColumn: index + 1, gridRow: 8 }}
              >
                {file.toUpperCase()}
              </span>
            ))}
            {rankLabels.map((rank, index) => (
              <span
                className="capy-board__coordinate-badge is-rank"
                data-board-coordinate={`rank-${rank}`}
                data-coordinate-axis="rank"
                data-coordinate-tone="fixed"
                key={`rank-${rank}`}
                style={{ gridColumn: 1, gridRow: index + 1 }}
              >
                {rank}
              </span>
            ))}
          </div>
        )}
        <div
          className={[
            'capy-board__semantic-grid',
            !inputEnabled ? projection.review ? 'is-review' : 'is-locked' : '',
          ].filter(Boolean).join(' ')}
          role="grid"
          aria-label={`${label}: กระดานควบคุมด้วยแป้นพิมพ์`}
          aria-describedby="capy-board-keyboard-help"
        >
          {Array.from({ length: 8 }, (_, rowIndex) => (
            <div className="capy-board__semantic-row" role="row" key={squares[rowIndex * 8]}>
              {squares.slice(rowIndex * 8, rowIndex * 8 + 8).map((square) => {
                const piece = pieces.get(square);
                const selected = selectedSquare === square;
                const legalTarget = legalTargets.has(square);
                const lastMoveRole = projection.lastMove?.from === square
                  ? 'from'
                  : projection.lastMove?.to === square ? 'to' : undefined;
                const checked = piece?.piece === 'k' && piece.color === checkedRulesColor(projection);
                const lockedState = !inputEnabled
                  ? projection.review ? 'review' : 'locked'
                  : undefined;

                return (
                  <div className="capy-board__semantic-cell" role="gridcell" key={square}>
                    <button
                      ref={(element) => {
                        if (element === null) {
                          squareRefs.current.delete(square);
                        } else {
                          squareRefs.current.set(square, element);
                        }
                      }}
                      className={[
                        'capy-board__semantic-square',
                        selected ? 'is-selected' : '',
                        legalTarget ? 'is-legal-target' : '',
                        checked ? 'is-checked' : '',
                        lockedState === undefined ? '' : `is-${lockedState}`,
                      ].filter(Boolean).join(' ')}
                      type="button"
                      tabIndex={focusedSquare === square ? 0 : -1}
                      aria-label={accessibleSquareName({
                        square,
                        piece,
                        selected,
                        legalTarget,
                        checked,
                        inputEnabled,
                        projection,
                      })}
                      aria-pressed={selected}
                      aria-disabled={!inputEnabled}
                      data-board-square={square}
                      data-selected={selected || undefined}
                      data-legal-target={legalTarget || undefined}
                      data-last-move={lastMoveRole}
                      data-checked={checked || undefined}
                      data-board-state={lockedState}
                      onClick={() => activateSquare(square)}
                      onKeyDown={(event) => onSquareKeyDown(event, square)}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <p id="capy-board-keyboard-help" className="capy-board__semantic-help">
        แป้นลูกศรย้ายโฟกัส, Enter หรือ Space เลือกต้นทางและปลายทาง, Esc ยกเลิก; กรอบคือโฟกัส, จุดคือปลายทางที่เดินได้, รุกคือคิงถูกรุก
      </p>
    </section>
  );
}
